import React, { memo, useCallback } from 'react';
import { css, useTheme } from "@emotion/react";

import { NOOP } from "core/constants";

type Props = {
    className?: string,
    hasError?: boolean,
    label: string,
    id?: string,
    name: string,
    onChange: (name: string, value: boolean) => void,
    value: boolean,
    isCircle?: boolean
};

const SimpleCheckbox: React.FC<Props> = (
    {
        className = '',
        label = '',
        id = '',
        name = '',
        onChange = NOOP,
        value = false,
        isCircle = false,
        hasError = false,
    }
) => {
    const theme = useTheme();

    const onClick = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();

        onChange(name, !value);
    }, [name, onChange, value]);

    const onKeyDown = useCallback((e: any) => {
        if (e.keyCode === 32) {
            onClick(e);
        }
    }, [onClick]);

    const checkboxContainerStyle = css`
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        outline: none;

        &:hover > div {
            border-color: ${theme.colours.blue[400]};
        }
    `;

    const checkboxStyle = ({
                               selected = false,
                               hasError = false
                           }: {
        selected?: boolean,
        hasError?: boolean
    } = {}) => css`
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        border-radius: ${isCircle ? '50%' : '3px'};
        display: inline-block;
        position: relative;
        background-color: ${selected ? theme.colours.curiousBlue : 'white'};
        border: 1px solid ${theme.colours.curiousBlue};
        transition: border-color ${theme.transitions.transitionFaster}, background-color ${theme.transitions.transitionFaster};

        ${hasError && css`
            border-color: ${theme.colours.crimson};
        `};

        ${!selected && css`
            &:hover {
                background-color: ${theme.colours.cornflowerBlue2};
            }
        `};
    `;

    const labelStyle = css`
        margin-left: 10px;
        width: calc(100% - 20px);
        line-height: 20px;
    `;

    const markStyle = css`
        height: 20px;
        width: 20px;
        background-color: ${theme.colours.white};

        &::after {
            content: "";
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -60%) rotate(45deg) skew(13deg);
            width: 5px;
            height: 9px;
            border: solid white;
            border-width: 0 2px 2px 0;
        }
    `;

    return (
        <div
            id={id || name}
            onClick={onClick}
            css={checkboxContainerStyle}
            onKeyDown={onKeyDown}
            tabIndex={-1}
            className={className}
        >
            <div css={checkboxStyle({ selected: value, hasError })}>
                {value && <span css={markStyle} />}
            </div>
            <span className={'option-text'} css={labelStyle}>{label}</span>
        </div>
    );
};

export default memo(SimpleCheckbox);